import { CloseCircleOutlined, FolderOutlined } from '@ant-design/icons'
import { Avatar, List } from 'antd'
import { isNaN, isNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Department } from '@/modules/department'
import { DepartmentUser } from '@/modules/project/project-create/project-form.component'
import { User, UserInfo } from '@/modules/user'

import {
  DepartmentTreeWithUsers,
  getDepartmentChildren,
  shouldUncheckParents,
} from './department-tree-with-users.component'

export const isIdPureNumber = (id: string | number) => {
  return isNumber(Number(id)) && !isNaN(Number(id))
}

export const DepartmentTreeTransfer = ({
  users,
  departments,
  userInfo,
  defaultPermissions = [],
  departmentUsers,
  setDepartmentUsers,
}: {
  users: User[]
  departments: Department[]
  userInfo: UserInfo
  defaultPermissions?: string[]
  departmentUsers: DepartmentUser[]
  setDepartmentUsers: React.Dispatch<React.SetStateAction<DepartmentUser[]>>
}) => {
  const { t } = useTranslation()
  const departmentIds = departmentUsers
    .filter((departmentUser) => isIdPureNumber(departmentUser.id))
    .map((departmentUser) => departmentUser.id)

  const left = (
    <DepartmentTreeWithUsers
      users={users}
      departments={departments}
      userInfo={userInfo}
      defaultPermissions={defaultPermissions}
      departmentUsers={departmentUsers}
      setDepartmentUsers={setDepartmentUsers}
    />
  )
  const right = (
    <>
      <div className='py-3'>
        <span className='text-gray-400 leading-6'>{t('departmentTree.selected')}</span>
      </div>
      <List
        dataSource={departmentUsers.filter((departmentUser) => {
          return isIdPureNumber(departmentUser.id) || departmentIds.every((id) => !departmentUser.id.startsWith(id))
        })}
        renderItem={(value) => {
          let departmentName = ''
          if (!isIdPureNumber(value.id)) {
            departmentName =
              departments.find((department) => department.id.toString() === value.id.split('-')[0])?.name || ''
          }

          return (
            <List.Item className='flex mx-2 items-center justify-between'>
              <div className='flex items-center'>
                <Avatar src={value.avatar} className='flex-shrink-0 mr-10' icon={!value.avatar && <FolderOutlined />} />
                <div className='flex flex-col'>
                  <span className='text-xs'>{value.name}</span>
                  <span className='text-xs text-[rgba(0,0,0,0.45)]'>{departmentName}</span>
                </div>
              </div>
              <CloseCircleOutlined
                className='cursor-pointer ease-linear text-18px text-red-500 duration-200 hover:text-red-600'
                onClick={() =>
                  setDepartmentUsers((prev) => {
                    let current = prev.filter(
                      (data) =>
                        data.id !== value.id &&
                        !data.id.startsWith(value.id) &&
                        shouldUncheckParents(departments, data.id, value.id)
                    )
                    if (isIdPureNumber(value.id)) {
                      const result = [] as DepartmentUser[]
                      getDepartmentChildren(value, departments, users, result)
                      current = current.filter((c) => result.every((r) => r.id !== c.id))
                    }
                    return current
                  })
                }
              />
            </List.Item>
          )
        }}
      />
    </>
  )
  return (
    <div className='flex w-full space-x-4 max-h-[60vh]'>
      <div className='flex-1 overflow-y-scroll scrollbar-thin'>{left}</div>
      <div className='flex-1 overflow-y-scroll scrollbar-thin'>{right}</div>
    </div>
  )
}
